import { Box, Paper, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from '@shared/components/spinner/Spinner';
import { IconHeadphones } from '@shared/assets/images/svgs';
import { TabSectionHeader } from './TabSectionHeader';
import {
  AgentDto,
  CampaignDto,
  HandoffRuleRequestDto,
  apiService,
  useHandoffRuleCreateControllerExecuteMutation,
} from '@shared/services/apiService/apiService';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { HandoffRulesEditor } from '@pages/Admin/Main/Agent/ui/components/HandoffRulesEditor';
import { SQUAD_CONFIG, SQUAD_MESSAGES } from '@features/Admin/Agent/constants/squad.constants';
import {
  parseHandoffRulesValue,
  pruneHandoffCanvasToReferencedAgents,
  serializeHandoffRules,
  serializeHandoffRulesDocument,
} from '@features/Admin/Agent/utils/handoff-rule-mapper';
import {
  saveHandoffCanvasLayout,
  withStoredHandoffCanvasLayout,
} from '@features/Admin/Agent/utils/handoffCanvasLayoutStorage';
import { useBeforeUnload } from '@shared/hooks/useBeforeUnload';
import { RouterBlockerDialog } from '@shared/hooks/useRouterBlockerDialog';

const handoffRulesPayloadMatches = (a: string, b: string): boolean => {
  const parsedA = parseHandoffRulesValue(a);
  const parsedB = parseHandoffRulesValue(b);
  if (parsedA.hasError || parsedB.hasError) return false;
  return (
    JSON.stringify(serializeHandoffRules(parsedA.rules)) ===
    JSON.stringify(serializeHandoffRules(parsedB.rules))
  );
};

const mergeApiHandoffRulesIntoDocument = (currentValue: string, apiRulesJson: string): string => {
  const current = parseHandoffRulesValue(currentValue);
  const api = parseHandoffRulesValue(apiRulesJson);
  if (api.hasError) return currentValue;
  if (current.hasError) return apiRulesJson;

  const pruned = pruneHandoffCanvasToReferencedAgents(api.rules, current.nodePositions);
  return JSON.stringify(
    serializeHandoffRulesDocument(api.rules, pruned.canvasAgentIds, pruned.nodePositions),
    null,
    2,
  );
};

interface SquadsTabProps {
  agents: AgentDto[];
  isLoading: boolean;
  campaign?: CampaignDto;
  refetch?: () => Promise<unknown>;
  onDirtyChange?: (isDirty: boolean) => void;
  onActionsChange?: (actions: { save: () => Promise<boolean>; discard: () => void } | null) => void;
  onRequestAgentsTab?: () => void;
}

const SquadsTab: FC<SquadsTabProps> = ({
  agents,
  isLoading,
  campaign,
  refetch,
  onDirtyChange,
  onActionsChange,
  onRequestAgentsTab,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentUserRole = useAppSelector((state) => state.auth.authenticatedUser?.role);
  const isDisabled = currentUserRole === UserRolesEnum.VIEWER;
  const squad = campaign?.squad;
  const [saveHandoffRules, { isLoading: isSaving }] =
    useHandoffRuleCreateControllerExecuteMutation();
  const [isDirty, setIsDirty] = useState(false);
  const optimisticSavedRulesRef = useRef<string | null>(null);

  const initialHandoffRules = useMemo(() => {
    const fromApi =
      squad?.handoffRules !== undefined
        ? JSON.stringify(squad.handoffRules, null, 2)
        : SQUAD_CONFIG.DEFAULT_HANDOFF_RULES;
    if (!squad?.id) return fromApi;
    return withStoredHandoffCanvasLayout(squad.id, fromApi);
  }, [squad?.handoffRules, squad?.id]);

  const [handoffRules, setHandoffRules] = useState(initialHandoffRules);
  const handoffRulesRef = useRef(handoffRules);
  const lastSyncedInitialRef = useRef<string>(initialHandoffRules);

  useEffect(() => {
    handoffRulesRef.current = handoffRules;
  }, [handoffRules]);

  // Keep the on-screen layout across full page refresh (API only stores rules).
  // Do not overwrite the stored layout with unsaved drafts that the user may discard.
  useEffect(() => {
    if (!squad?.id || isDirty) return;
    const parsed = parseHandoffRulesValue(handoffRules);
    if (parsed.hasError) return;
    if (Object.keys(parsed.nodePositions).length === 0) return;
    saveHandoffCanvasLayout(squad.id, {
      canvasAgentIds: parsed.canvasAgentIds,
      nodePositions: parsed.nodePositions,
    });
  }, [handoffRules, isDirty, squad?.id]);

  useEffect(() => {
    // Keep local edits stable while the user is working; campaign refreshes can
    // otherwise overwrite keystrokes in the structured editor.
    const userHasLocalEdits =
      handoffRules !== lastSyncedInitialRef.current && !optimisticSavedRulesRef.current;
    if (isDirty || userHasLocalEdits) return;
    if (handoffRules === lastSyncedInitialRef.current && !optimisticSavedRulesRef.current) {
      return;
    }
    if (optimisticSavedRulesRef.current) {
      if (handoffRulesPayloadMatches(optimisticSavedRulesRef.current, initialHandoffRules)) {
        optimisticSavedRulesRef.current = null;
        const mergedValue = mergeApiHandoffRulesIntoDocument(
          handoffRulesRef.current,
          initialHandoffRules,
        );
        setHandoffRules(mergedValue);
        lastSyncedInitialRef.current = mergedValue;
        return;
      }
      setHandoffRules(optimisticSavedRulesRef.current);
      lastSyncedInitialRef.current = optimisticSavedRulesRef.current;
      return;
    }
    setHandoffRules(initialHandoffRules);
    lastSyncedInitialRef.current = initialHandoffRules;
    setIsDirty(false);
  }, [handoffRules, initialHandoffRules, isDirty]);

  useEffect(() => {
    optimisticSavedRulesRef.current = null;
  }, [squad?.id]);

  const handleSave = useCallback(
    async (payload: HandoffRuleRequestDto[]) => {
      if (!squad?.id || isDisabled) {
        return { ok: false, message: t(SQUAD_MESSAGES.HANDOFF_RULES_SAVE_FAILED) };
      }
      const response = await saveHandoffRules({ id: squad.id, body: payload });
      if ('error' in response) {
        return { ok: false, message: t(SQUAD_MESSAGES.HANDOFF_RULES_SAVE_FAILED) };
      }
      const currentDocument = parseHandoffRulesValue(handoffRulesRef.current);
      const rulesForDocument = currentDocument.hasError
        ? parseHandoffRulesValue(JSON.stringify(payload)).rules
        : currentDocument.rules;
      const pruned = pruneHandoffCanvasToReferencedAgents(
        rulesForDocument,
        currentDocument.hasError ? {} : currentDocument.nodePositions,
      );
      const patchedDocument = serializeHandoffRulesDocument(
        rulesForDocument,
        pruned.canvasAgentIds,
        pruned.nodePositions,
      );
      const savedSerializedValue = JSON.stringify(patchedDocument, null, 2);
      handoffRulesRef.current = savedSerializedValue;
      setHandoffRules(savedSerializedValue);
      optimisticSavedRulesRef.current = savedSerializedValue;
      lastSyncedInitialRef.current = savedSerializedValue;
      setIsDirty(false);

      if (campaign?.id) {
        saveHandoffCanvasLayout(squad.id, {
          canvasAgentIds: pruned.canvasAgentIds,
          nodePositions: pruned.nodePositions,
        });
        dispatch(
          apiService.util.updateQueryData(
            'campaignControllerCampaign',
            { id: campaign.id },
            (draft) => {
              if (!draft?.squad) return;
              // Keep canvas layout in cache so in-session refetches stay consistent.
              draft.squad.handoffRules =
                patchedDocument as unknown as typeof draft.squad.handoffRules;
            },
          ),
        );
      }

      try {
        await refetch?.();
      } catch {
        // Save succeeded; campaign cache was patched optimistically above.
      }
      return { ok: true, message: t(SQUAD_MESSAGES.HANDOFF_RULES_SAVED) };
    },
    [campaign?.id, dispatch, isDisabled, refetch, saveHandoffRules, squad?.id, t],
  );

  const handleDirtyChange = useCallback(
    (dirty: boolean) => {
      setIsDirty(dirty);
      onDirtyChange?.(dirty);
    },
    [onDirtyChange],
  );

  const handleEditorActionsChange = useCallback(
    (actions: { save: () => Promise<boolean>; discard: () => void } | null) => {
      onActionsChange?.(
        actions
          ? {
              save: actions.save,
              discard: () => {
                optimisticSavedRulesRef.current = null;
                const savedParsed = parseHandoffRulesValue(lastSyncedInitialRef.current);
                if (
                  squad?.id &&
                  !savedParsed.hasError &&
                  Object.keys(savedParsed.nodePositions).length > 0
                ) {
                  saveHandoffCanvasLayout(squad.id, {
                    canvasAgentIds: savedParsed.canvasAgentIds,
                    nodePositions: savedParsed.nodePositions,
                  });
                }
                setHandoffRules(lastSyncedInitialRef.current);
                setIsDirty(false);
                onDirtyChange?.(false);
                actions.discard();
              },
            }
          : null,
      );
    },
    [onActionsChange, onDirtyChange],
  );

  useBeforeUnload(() => isDirty);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box width="100%" sx={{ padding: 4 }}>
      <RouterBlockerDialog
        isBlocked={isDirty}
        titleKey="dialog.title2"
        message={t('campaignPage.squads.editor.unsavedLeaveWarning')}
      />
      {!squad?.id ? (
        <>
          <TabSectionHeader icon={IconHeadphones} title={t('campaignPage.editCampaign.squads')} />
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('adminMainPage.squad.descriptions.singleAgentConfig')}
            </Typography>
          </Paper>
        </>
      ) : (
        <HandoffRulesEditor
          value={handoffRules}
          onChange={setHandoffRules}
          isDisabled={isDisabled}
          agents={agents}
          isSaving={isSaving}
          onSave={handleSave}
          onDirtyChange={handleDirtyChange}
          onActionsChange={handleEditorActionsChange}
          onRequestAgentsTab={onRequestAgentsTab}
        />
      )}
    </Box>
  );
};

export default SquadsTab;
